import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import PostCard from '../components/PostCard';
import SkeletonLoader from '../components/SkeletonLoader';
import EmptyState from '../components/EmptyState';
import { cn } from '../utils';
import { Search, X } from 'lucide-react';

export default function Home() {
  const { posts, token, currentUser, selectedUniversity, setSelectedUniversity, fetchPosts, hasMore, isFetchingMore, fetchRandomPosts } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = React.useRef(null);
  const loaderRef = React.useRef(null);

  // Handle URL query parameter ?q=search_term
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q) {
      setSearchQuery(q);
      // Remove the query param from URL without refreshing to keep it clean
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const [isLooping, setIsLooping] = useState(false);

  // Debounce search
  useEffect(() => {
    setIsLooping(false); // Reset looping on search
    const timer = setTimeout(() => {
      fetchPosts(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isFetchingMore) {
        if (hasMore) {
          fetchPosts(searchQuery, true);
        } else if (!searchQuery) { 
          // If no more normal posts and not searching, start random loop
          setIsLooping(true);
          fetchRandomPosts(true);
        }
      }
    }, { threshold: 0.1 });

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [hasMore, isFetchingMore, searchQuery, fetchPosts, fetchRandomPosts]);

  return (
    <div className="flex flex-col">
      <div className="p-4 border-b border-slate-100 dark:border-slate-900/50 flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight">Home</h1>
        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-xl">
          <button 
            onClick={() => setSelectedUniversity(null)}
            className={cn(
              "px-4 py-1.5 rounded-lg text-sm font-bold transition-all",
              !selectedUniversity ? "bg-white dark:bg-selffo-dark text-slate-900 dark:text-white shadow-sm" : "text-slate-500"
            )}
          >
            Global
          </button>
          <button 
            onClick={() => {
               if (!token) navigate('/login');
               else setSelectedUniversity(currentUser?.university);
            }}
            className={cn(
              "px-4 py-1.5 rounded-lg text-sm font-bold transition-all",
              selectedUniversity ? "bg-white dark:bg-selffo-dark text-slate-900 dark:text-white shadow-sm" : "text-slate-500"
            )}
          >
            My Campus
          </button>
        </div>
      </div>
      <div className="px-4 py-3 bg-white dark:bg-selffo-dark border-b border-slate-100 dark:border-slate-900/50">
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            searchInputRef.current?.blur(); // Strongly force hide keyboard
          }}
          className="relative flex items-center group"
        >
          <Search className={cn(
            "absolute left-3 w-4 h-4 transition-colors",
            isSearching ? "text-slate-900 dark:text-white" : "text-slate-400 dark:text-slate-600"
          )} />
          <input 
            ref={searchInputRef}
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearching(true)}
            onBlur={() => setIsSearching(false)}
            placeholder={`Search ${selectedUniversity || 'everything'}...`}
            className="w-full bg-slate-100 dark:bg-slate-900/50 border-none rounded-xl py-2.5 pl-10 pr-10 text-sm focus:ring-2 focus:ring-slate-100 dark:focus:ring-slate-800 transition-all placeholder:text-slate-500"
          />
          {searchQuery && (
            <button 
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 p-0.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-all"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          )}
        </form>
      </div>
      <div 
        className="w-full bg-white dark:bg-selffo-dark p-4 border-b border-slate-100 dark:border-slate-900/50 flex gap-3 cursor-text hover:bg-slate-50 dark:hover:bg-slate-900/20 transition-all"
        onClick={() => {
          if (!token) {
             navigate('/login');
          } else {
             window.document.getElementById('create-post-btn')?.click(); 
          }
        }}
      >
        <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-900 flex-shrink-0 flex items-center justify-center text-slate-500">
          <span className="text-lg">✍️</span>
        </div>
        <div className="flex items-center text-slate-500 dark:text-slate-500 font-medium w-full text-lg mt-0.5">
          Share a confession...
        </div>
      </div>

      {!posts ? (
        <SkeletonLoader />
      ) : posts.length === 0 ? (
        <EmptyState 
          message={selectedUniversity ? `Quiet at ${selectedUniversity}` : "Silence in the air"} 
          submessage={selectedUniversity ? "Be the first to break the silence on your campus." : "Start the conversation with your first confession."}
        />
      ) : (
        <div className="flex flex-col">
          {posts.map(post => (
            <PostCard key={post.id} post={post} />
          ))}
          
          {(hasMore || isLooping) && (
            <div ref={loaderRef} className="p-8 flex flex-col items-center justify-center border-b border-slate-100 dark:border-slate-900/50">
              {isFetchingMore && (
                <div className="flex space-x-2 items-center justify-center mb-4">
                  <div className="w-2.5 h-2.5 bg-indigo-400 dark:bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2.5 h-2.5 bg-indigo-400 dark:bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2.5 h-2.5 bg-indigo-400 dark:bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              )}
              {isLooping && !isFetchingMore && <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Discovering more secrets...</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
